import {
  Card,
  Flex,
  Text,
  Input,
  Select,
  Heading,
  Checkbox,
  FormLabel,
  FormControl,
  CurrencyInput,
  FormHelperText
} from '@homebotapp/hb-react-component-catalog'
import { useEffect, useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { selectStreet } from '../../../store/selectors/home'
import { fetchCompiledHome } from '../../../actions/homeCreate'
import { formatAbbrCurrency } from '../../../helpers/formatters'
import { selectClientHomes } from '../../../store/selectors/client'
import { selectTotalEquity } from '../../../store/selectors/home/equity'

export const MSG = defineMessages({
  yourInfo: {
    id: 'AffordabilityCalculator.yourInfo',
    defaultMessage: 'Your information'
  },
  yearlyIncome: {
    id: 'AffordabilityCalculator.yearlyIncome',
    defaultMessage: 'Yearly income (pre-tax)'
  },
  creditScore: {
    id: 'AffordabilityCalculator.creditScore',
    defaultMessage: 'Credit score'
  },
  downPayment: {
    id: 'AffordabilityCalculator.downPayment',
    defaultMessage: 'Down payment (dollars)'
  },
  monthlyDebt: {
    id: 'AffordabilityCalculator.monthlyDebt',
    defaultMessage: 'Monthly expenses'
  },
  totalMonthlyDebtHelperText: {
    id: 'AffordabilityCalculator.totalMonthlyDebtHelperText',
    defaultMessage: ' Credit cards, car loans, student loans, etc.'
  },
  useEquity: {
    id: 'AffordabilityCalculator.useEquity',
    defaultMessage: 'Use {equity} in equity from {address}'
  },
  location: {
    id: 'AffordabilityCalculator.location',
    defaultMessage: 'Location'
  },
  locationPlaceholder: {
    id: 'AffordabilityCalculator.locationPlaceholder',
    defaultMessage: 'ZIP Code'
  },
  locationDesc: {
    id: 'AffordabilityCalculator.locationDesc',
    defaultMessage: 'We will pull tax and insurance estimates for your area'
  }
})

export interface AffordabilityCalculatorYourInformationSectionProps {
  zipCode: string
  setZipCode: (zipCode: string) => void
  creditScore: string
  setCreditScore: (creditScore: string) => void
  monthlyDebt: string
  setMonthlyDebt: (monthlyDebt: string) => void
  downPayment: string
  setDownPayment: (downPayment: string) => void
  yearlyIncome: string
  setYearlyIncome: (yearlyIncome: string) => void
}

export const AffordabilityCalculatorYourInformationSection = ({
  zipCode,
  setZipCode,
  creditScore,
  setCreditScore,
  monthlyDebt,
  setMonthlyDebt,
  downPayment,
  setDownPayment,
  yearlyIncome,
  setYearlyIncome
}: AffordabilityCalculatorYourInformationSectionProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const homes = useSelector(selectClientHomes)
  const equity = useSelector(selectTotalEquity)
  const addressStreet = useSelector(selectStreet)
  const [useEquity, setUseEquity] = useState(false)

  useEffect(() => {
    if (homes?.[0]?.id) {
      dispatch(fetchCompiledHome(homes[0]?.id, false))
    }
  }, [homes, dispatch])

  return (
    <Card p={4}>
      <Heading as='h4' size={['2xs', 'xs', 'sm']}>
        {intl.formatMessage(MSG.yourInfo)}
      </Heading>
      <Flex mt={4} gap={4} direction='column' alignItems='flex-start'>
        <Flex width='100%' direction='column' gap={2} alignItems='flex-start'>
          <Flex gap={4} width='100%' direction={['column', null, null, 'row']}>
            <FormControl>
              <FormLabel size='xs'>{intl.formatMessage(MSG.yearlyIncome)}</FormLabel>
              <CurrencyInput
                min={0}
                size='sm'
                decimalScale={2}
                name='yearlyIncome'
                value={yearlyIncome}
                onChange={val => {
                  // @ts-ignore
                  setYearlyIncome(val)
                  // @ts-ignore
                  localStorage.setItem('affordabilityCalculatorYearlyIncome', val)
                }}
                tracking={{
                  guid: 'enaiiqVnEJA9irDepeqqK2',
                  ui_context: 'Affordability Calculator Page',
                  descriptor: 'Yearly income input field'
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel size='xs'>{intl.formatMessage(MSG.creditScore)}</FormLabel>
              <Select
                size='sm'
                name='creditScore'
                value={creditScore}
                onChange={e => {
                  setCreditScore(e.target.value)
                  localStorage.setItem('affordabilityCalculatorCreditScore', e.target.value)
                }}
                tracking={{
                  guid: 'cjP3EXgaPLUTUnCTGNz6iu',
                  ui_context: 'Affordability Calculator Page',
                  descriptor: 'Credit score select'
                }}
              >
                <option value={740}>740+</option>
                <option value={720}>720 - 739</option>
                <option value={700}>700 - 719</option>
                <option value={680}>680 - 699</option>
                <option value={660}>660 - 679</option>
                <option value={640}>640 - 659</option>
                <option value={620}>300 - 639</option>
              </Select>
            </FormControl>
          </Flex>
        </Flex>
        <Flex mb={2} gap={4} width='100%' direction={['column', null, null, 'row']}>
          <FormControl>
            <FormLabel size='xs'>{intl.formatMessage(MSG.downPayment)}</FormLabel>
            <CurrencyInput
              min={0}
              size='sm'
              decimalScale={2}
              name='downPayment'
              value={downPayment}
              onChange={val => {
                // @ts-ignore
                setDownPayment(val)
                // @ts-ignore
                localStorage.setItem('affordabilityCalculatorDownPayment', val)
              }}
              tracking={{
                guid: '7PbtcJM1cPpdgKvfhfuWBe',
                ui_context: 'Affordability Calculator Page',
                descriptor: 'Down payment input field'
              }}
            />
            {!!equity && !!addressStreet && (
              <FormHelperText>
                <Checkbox
                  size='sm'
                  name='useEquity'
                  checked={useEquity}
                  onChange={() => {
                    const newUseEquity = !useEquity
                    setUseEquity(newUseEquity)

                    if (newUseEquity) {
                      setDownPayment(equity.toString())
                    }
                  }}
                  tracking={{
                    guid: 'cvJk97mtiWVW6sfvqKLpyZ',
                    ui_context: 'Affordability Calculator Page',
                    descriptor: 'Down payment use equity checkbox'
                  }}
                >
                  <Text as='span' size='2xs'>
                    {intl.formatMessage(MSG.useEquity, {
                      equity: (
                        <Text as='span' size='2xs' fontWeight='bold' color='success.700'>
                          {formatAbbrCurrency(equity)}
                        </Text>
                      ),
                      address: addressStreet
                    })}
                  </Text>
                </Checkbox>
              </FormHelperText>
            )}
          </FormControl>
          <FormControl>
            <FormLabel size='xs'>{intl.formatMessage(MSG.monthlyDebt)}</FormLabel>
            <CurrencyInput
              min={0}
              size='sm'
              decimalScale={2}
              name='monthlyDebt'
              value={monthlyDebt}
              onChange={val => {
                // @ts-ignore
                setMonthlyDebt(val)
                // @ts-ignore
                localStorage.setItem('affordabilityCalculatorMonthlyDebt', val)
              }}
              tracking={{
                guid: '21EJvRY8vJjipQYG5EUQKW',
                ui_context: 'Affordability Calculator Page',
                descriptor: 'Monthly debt input field'
              }}
            />
            <FormHelperText fontSize='2xs'>{intl.formatMessage(MSG.totalMonthlyDebtHelperText)}</FormHelperText>
          </FormControl>
        </Flex>
        <FormControl>
          <FormLabel size='xs'>{intl.formatMessage(MSG.location)}</FormLabel>
          <Input
            size='sm'
            type='number'
            name='location'
            value={zipCode}
            placeholder={intl.formatMessage(MSG.locationPlaceholder)}
            onChange={e => {
              setZipCode(e.target.value)
              localStorage.setItem('affordabilityCalculatorZipCode', e.target.value)
            }}
            tracking={{
              descriptor: 'Location',
              guid: 'fWzvPELz1fPLju18Z7sWdi',
              ui_context: 'Affordability Calculator Page'
            }}
          />
          <FormHelperText>
            <Text mb={0} size='2xs'>
              {intl.formatMessage(MSG.locationDesc)}
            </Text>
          </FormHelperText>
        </FormControl>
      </Flex>
    </Card>
  )
}
